import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import LMSHeader from '../components/LMSHeader';
import CourseCard from '../components/CourseCard';
import TestimonialCard from '../components/TestimonialCard';
import heroDesktop from "../assets/images/bg-hero-desktop-two.jpg";
import heroMobile from "../assets/images/bg-hero-mobile.jpg";
import useCourseStore from '../stores/useCourseStore';
import useAuthStore from '../stores/useAuthStore';
import FloatingChatButton from '../components/FloatingChatButton';


const HomePage = () => {
  const navigate = useNavigate();
  const user = useAuthStore(state => state.user);
  const {
    courses: allCourses,
    fetchCourses,
    categories: allCategories,
    fetchCategories,
    isLoading: coursesLoading
  } = useCourseStore();

  const { token } = useAuthStore();

  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isTutorVideoModalOpen, setIsTutorVideoModalOpen] = useState(false);

  const enrolledCourses = useCourseStore(state => state.enrolledCourses);
  const fetchMyCourses = useCourseStore(state => state.fetchMyCourses);

  const [initialLoading, setInitialLoading] = useState(true);

  const [bgImage, setBgImage] = useState(heroDesktop);
  const [activeCategory, setActiveCategory] = useState('all');
  const [stats, setStats] = useState({
    students: 0,
    courses: 0,
    instructors: 0,
    certificates: 0
  });

  const filteredCourses = useMemo(() => {
    if (activeCategory === 'all') return allCourses;
    return allCourses.filter(course => {
      const cat = course.category;
      if (!cat) return false;
      if (cat.name && cat.name === activeCategory) return true;
      return false;
    });
  }, [allCourses, activeCategory]);

  let oilAndGasCategories = [];

  if (allCategories.length > 0) {
    const oilAndGasRoot = allCategories.find(
      (cat) =>
        cat.name.toLowerCase() === "oil and gas courses" ||
        cat.slug === "oil-and-gas-courses"
    );

    if (oilAndGasRoot) {
      const flattenCategories = (category) => {
        let result = [];
        if (category.children && category.children.length > 0) {
          category.children.forEach((child) => {
            result.push(child);
            result = result.concat(flattenCategories(child));
          });
        }
        return result;
      };

      oilAndGasCategories = flattenCategories(oilAndGasRoot);
    }
  }

  const categories = [
    "all",
    ...(oilAndGasCategories?.length ? oilAndGasCategories.map((cat) => cat.name) : []),
  ];

  useEffect(() => {
    if (user?.role === "student") {
      const loadCourseData = async () => { await fetchMyCourses() };
      loadCourseData();
    }
  }, [user]);

  useEffect(() => {
    fetchCourses({ order: 'desc', limit: 6 });
    fetchCategories();
    const timeout = setTimeout(() => setInitialLoading(false), 800);
    return () => clearTimeout(timeout);
  }, [fetchCourses, fetchCategories]);


  const handleEnroll = async (courseSlug, courseId) => {
    if (!token) {
      localStorage.setItem('redirectAfterLogin', `/courses/${courseSlug}/${courseId}`);
      navigate('/login');
      return;
    }
    try {
      const result = await useCourseStore.getState().enrollInCourse(courseId);
      if (result?.data?.authorizationUrl) {
        window.location.href = result.data.authorizationUrl;
      } else {
        alert('Enrolled successfully!');
      }
    } catch (err) {
      console.error('Enrollment failed:', err);
      alert('Failed to enroll in course');
    }
  };


  useEffect(() => {
    const updateBackground = () => {
      if (window.innerWidth <= 768) {
        setBgImage(heroMobile);
      } else {
        setBgImage(heroDesktop);
      }
    };

    updateBackground();
    window.addEventListener("resize", updateBackground);

    return () => window.removeEventListener("resize", updateBackground);
  }, []);

  const features = [
    {
      icon: '🎓',
      title: 'Expert Instructors',
      description: 'Gain insights from seasoned professionals who bring real-world experience and proven expertise & teaching skills to every course.'
    },
    {
      icon: '📚',
      title: 'Comprehensive Course Library',
      description: 'Discover a wide-ranging catalog of accredited, expertly curated courses, customized to meet diverse learning goals and consistently updated to reflect the latest industry standards and emerging trends.'
    },
    {
      icon: '⏱️',
      title: 'Self-Paced Learning',
      description: 'Self-Paced Learning Take control of your learning journey, study anytime, anywhere, and at your own speed with our fully mobile and desktop optimized platform. '
    },
    {
      icon: '🎥',
      title: 'HD Video Lessons',
      description: 'High-quality video content with multiple resolutions (240p to 1080p) for optimal viewing'
    },
    {
      icon: '💬',
      title: 'Community Support',
      description: 'Connect with instructors and fellow students through discussion forums and live Q&A'
    },
    {
      icon: '💰',
      title: 'Affordable Pricing',
      description: 'Get world-class learning at a fraction of the cost, take advantage of discounted courses and unlock financial support through exclusive coupons and vouchers. '
    },
  ];

  const testimonials = [
    {
      id: 1,
      name: 'Emma Johnson',
      role: 'Student',
      rating: 4.5,
      content: 'BoleumLearn completely transformed my career. I went from zero coding experience to landing my first developer job in just 6 months! The instructors are amazing and always available to help.'
    },
    {
      id: 2,
      name: 'Sarah Aghogho',
      role: 'Instructor',
      rating: 5,
      content: 'Teaching on BoleumLearn has been an incredible experience. The platform makes it easy to create engaging content, track student progress, and connect with learners worldwide. The video upload system is fantastic!'
    },
    {
      id: 3,
      name: 'Michael Adesina',
      role: 'Student',
      rating: 4,
      content: 'The course content is top-notch, and the instructors are very responsive. I particularly love the multi-resolution video player - it works perfectly even on my slow internet connection. Highly recommend!'
    },
    {
      id: 4,
      name: 'Dr. James Omosede',
      role: 'Instructor',
      rating: 4.5,
      content: 'As an educator, I appreciate the robust grading system and assignment management tools. The admin approval process is smooth, and the revenue sharing is fair. BoleumLearn is the best platform I\'ve taught on.'
    },
  ];


  // Animate stats on page load
  useEffect(() => {
    const targets = {
      students: 50000,
      courses: 500,
      instructors: 200,
      certificates: 25000
    };

    const duration = 2000; // 2 seconds
    const steps = 60; // 60 steps for smooth animation
    const stepDuration = duration / steps;

    let currentStep = 0;
    const interval = setInterval(() => {
      currentStep++;
      setStats({
        students: Math.floor((targets.students * currentStep) / steps),
        courses: Math.floor((targets.courses * currentStep) / steps),
        instructors: Math.floor((targets.instructors * currentStep) / steps),
        certificates: Math.floor((targets.certificates * currentStep) / steps)
      });

      if (currentStep >= steps) {
        clearInterval(interval);
        setStats(targets);
      }
    }, stepDuration);

    return () => clearInterval(interval);
  }, []);


  return (
    <div className="min-h-screen flex flex-col">
      <LMSHeader />

      {isVideoModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setIsVideoModalOpen(false)}
        >
          <div
            className="relative w-full max-w-4xl bg-black rounded-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setIsVideoModalOpen(false)}
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70"
              aria-label="Close video"
            >
              ✕
            </button>
            <video
              src="/video/intro-video.mp4"
              controls
              controlsList="nodownload"
              disablePictureInPicture
              onContextMenu={(e) => e.preventDefault()}
              className="w-full aspect-video"
              preload="metadata"
            />
          </div>
        </div>
      )}

      {isTutorVideoModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
          onClick={() => setIsTutorVideoModalOpen(false)}
        >
          <div
            className="relative w-full max-w-4xl bg-black rounded-xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setIsTutorVideoModalOpen(false)}
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
              aria-label="Close video"
            >
              ✕
            </button>
            <video
              src="/video/onboarding.mp4"
              controls
              controlsList="nodownload "
              disablePictureInPicture
              onContextMenu={(e) => e.preventDefault()}
              className="w-full aspect-video"
              preload="metadata"
            />
          </div>
        </div>
      )}

      <main className="flex-1">
        {/* Hero Section with Background Image */}
        <section
          className="relative bg-gradient-to-r from-primary-600 to-primary-700 text-white py-20 overflow-hidden"
          style={{
            backgroundImage: `url(${bgImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center center",
            backgroundRepeat: "no-repeat",
          }}
        >

          {/* Dark Overlay */}
          <div className="absolute inset-0 bg-black/65 z-0"></div>

          {/* Animated Background Elements */}
          <div className="absolute inset-0 overflow-hidden z-0">
            <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-600 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
            <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-primary-600 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
          </div>

          {/* Hero Content */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Empower Your Future with
              <span className="block text-primary-600">Expert-Guided Online Learning</span>
            </h1>

            <p className="text-xl md:text-xl mb-8 max-w-3xl mx-auto leading-relaxed">
              Join a thriving community of learners who are leveling up their careers through our dynamic courses, hands-on training, and world-class instructors. Whether you're upskilling, reskilling, or just starting out, our flexible platform is designed to fit your goals and your schedule.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/courses"
                className="px-8 py-4 bg-primary-600 text-white font-medium text-lg rounded-lg shadow-lg hover:bg-primary-700  transition-transform transform hover:scale-105 hover:shadow-xl"
              >
                Explore Courses
              </Link>
              <Link
                to="/register"
                className="px-8 py-4 bg-transparent border-2 border-white text-white font-medium text-lg rounded-lg hover:bg-white hover:text-primary-600 transition-transform transform hover:scale-105 hover:shadow-lg"
              >
                Get Started - It's Free!
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="mt-12 flex flex-wrap justify-center gap-8 text-sm">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <span>4.9/5 Average Rating</span>
              </div>
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span>Certificate of Completion</span>
              </div>
            </div>
          </div>
        </section>


        {/* Features Section */}
        <section className="py-16 bg-neutral-50 dark:bg-neutral-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-neutral-900 dark:text-white mb-4">Why Choose BoleumLearn?</h2>
              <p className="text-xl text-neutral-600 dark:text-neutral-400">Discover what makes our platform the best choice for online learning</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm p-6 hover:shadow-md transition transform hover:-translate-y-1">
                  <div className="text-5xl mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-3">{feature.title}</h3>
                  <p className="text-neutral-600 dark:text-neutral-400">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="relative py-20 overflow-hidden bg-neutral-900">
          {/* Static Background (Poster Image) */}
          <div
            className="absolute inset-0 z-0 bg-cover bg-center"
            style={{ backgroundImage: `url('/images/video-poster.jpg')` }}
          >
            <div className="absolute inset-0 bg-black/60"></div>
          </div>

          {/* Play Button */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center">
              <button
                onClick={() => setIsVideoModalOpen(true)}
                className="relative group focus:outline-none"
                aria-label="Play promotional video"
              >
                {/* Main Play Button */}
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:bg-white/30 transition-all duration-300">
                  <div className="ml-1 text-white text-2xl">▶</div>
                </div>
                {/* Pulse Animation */}
                <div className="absolute inset-0 w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-white/30 animate-ping opacity-75"></div>
                <div
                  className="absolute inset-0 w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-white/30 animate-ping opacity-50"
                  style={{ animationDelay: '1s' }}
                ></div>
              </button>
              <p className="mt-6 text-xl text-white max-w-2xl mx-auto">
                See how BoleumLearn transforms careers in under 2 minutes
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 bg-primary-600 dark:bg-neutral-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Your Learning Journey</h2>
              <p className="text-xl opacity-90">Simple steps to career transformation</p>
            </div>

            {/* Timeline */}
            <div className="relative">
              <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-white/30 top-0"></div>

              <div className="space-y-12 md:space-y-0">
                {[
                  {
                    step: 1,
                    title: "Register",
                    description: "Create your free account in seconds",
                    icon: "👤"
                  },
                  {
                    step: 2,
                    title: "Buy Course",
                    description: "Choose a course that fits your goals and budget",
                    icon: "🛒"
                  },
                  {
                    step: 3,
                    title: "Study at Your Pace",
                    description: "Learn anytime, anywhere — online or offline",
                    icon: "⏱️"
                  },
                  {
                    step: 4,
                    title: "Complete Modules",
                    description: "Progress through interactive lessons and quizzes",
                    icon: "✅"
                  },
                  {
                    step: 5,
                    title: "Get Your Certificate",
                    description: "Earn accredited certification upon completion",
                    icon: "🎓"
                  }
                ].map((item, index) => (
                  <div
                    key={item.step}
                    className={`flex flex-col md:flex-row items-center ${index % 2 === 0 ? 'md:flex-row-reverse' : ''}`}
                  >
                    {/* Content */}
                    <div className={`md:w-5/12 text-center md:text-${index % 2 === 0 ? 'right' : 'left'} mb-6 md:mb-0`}>
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm text-2xl mb-4">
                        {item.icon}
                      </div>
                      <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                      <p className="opacity-90">{item.description}</p>
                    </div>

                    {/* Dot */}
                    <div className="md:w-2/12 flex justify-center">
                      <div className="w-8 h-8 rounded-full bg-white border-4 border-primary-500 flex items-center justify-center">
                        <span className="text-primary-500 font-bold">{item.step}</span>
                      </div>
                    </div>

                    {index % 2 === 0 ? (
                      <div className="hidden md:block md:w-5/12 border-t-2 border-dashed border-white/40 mr-4"></div>
                    ) : (
                      <div className="hidden md:block md:w-5/12 border-t-2 border-dashed border-white/40 ml-4"></div>
                    )}

                    <div className="md:w-5/12"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Statistics Section */}
        <section className="py-16 bg-primary-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Join Our Global Learning Community</h2>
              <p className="text-xl opacity-90">Thousands of students trust us for their education</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-5xl font-bold mb-2">{stats.students.toLocaleString()}</div>
                <div className="text-xl font-medium">Students Enrolled</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold mb-2">{stats.courses}</div>
                <div className="text-xl font-medium">Courses Available</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold mb-2">{stats.instructors}</div>
                <div className="text-xl font-medium">Expert Instructors</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold mb-2">{stats.certificates.toLocaleString()}</div>
                <div className="text-xl font-medium">Certificates Issued</div>
              </div>
            </div>
          </div>
        </section>

        {/* Popular Courses Section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-neutral-900 dark:text-white mb-4">Courses</h2>
              <p className="text-xl text-neutral-600 dark:text-neutral-400">Start learning today</p>
            </div>

            {/* Category Filter - Draggable Horizontal Slider */}
            <div
              className="overflow-x-auto scrollbar-hide mb-8 cursor-grab active:cursor-grabbing"
              ref={(el) => {
                if (el) {
                  let isDown = false;
                  let startX;
                  let scrollLeft;

                  el.addEventListener('mousedown', (e) => {
                    isDown = true;
                    el.classList.add('active');
                    startX = e.pageX - el.offsetLeft;
                    scrollLeft = el.scrollLeft;
                  });

                  el.addEventListener('mouseleave', () => {
                    isDown = false;
                    el.classList.remove('active');
                  });

                  el.addEventListener('mouseup', () => {
                    isDown = false;
                    el.classList.remove('active');
                  });

                  el.addEventListener('mousemove', (e) => {
                    if (!isDown) return;
                    e.preventDefault();
                    const x = e.pageX - el.offsetLeft;
                    const walk = (x - startX) * 1; // scroll speed multiplier
                    el.scrollLeft = scrollLeft - walk;
                  });
                }
              }}
            >
              <div className="flex space-x-2 px-4 w-max select-none">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setActiveCategory(category)}
                    className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeCategory === category
                      ? 'bg-primary-500 text-white'
                      : 'bg-neutral-200 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-300 dark:hover:bg-neutral-600'
                      }`}
                  >
                    {category === 'all' ? 'All Categories' : category}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-6">
              {(coursesLoading || initialLoading || !filteredCourses) ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500"></div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredCourses.length > 0 ? (
                    filteredCourses.map((course) => (
                      <CourseCard
                        key={course._id}
                        course={course}
                        onEnroll={() => handleEnroll(course?.slug, course?._id)}
                        isEnrolled={enrolledCourses.some(c => c._id === course._id) ? true : false}
                      />
                    ))
                  ) : (
                    <div className="col-span-full text-center text-neutral-600 dark:text-neutral-400">
                      <div className="mr-4 mt-1 mb-8">
                        <span className="text-6xl">📚</span>
                      </div>
                      <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">No courses found</h3>
                      <h3 className="text-neutral-600 dark:text-neutral-400">
                        There are no courses in this category yet.
                      </h3>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="text-center mt-8">
              <Link
                to="/courses"
                className="inline-block px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-900 transition-all duration-200"
              >
                View All Courses
              </Link>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-16 bg-neutral-50 dark:bg-neutral-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-neutral-900 dark:text-white mb-4">What Our Community Says</h2>
              <p className="text-xl text-neutral-600 dark:text-neutral-400">Hear from our successful learners and instructors</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {testimonials.map((testimonial) => (
                <TestimonialCard key={testimonial.id} testimonial={testimonial} />
              ))}
            </div>
          </div>
        </section>


        {/*become a instructor */}
        <section className="py-16 bg-blue-800 text-neutral-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Become an Instructor</h2>
              <p className="text-xl opacity-90">
                Share your expertise, inspire learners, and earn revenue by teaching on BoleumLearn.
              </p>
            </div>

            <div className="bg-blue-900/20 backdrop-blur-sm dark:bg-neutral-900 rounded-2xl border border-white/20 p-8 md:p-12">
              <div className="flex flex-col lg:flex-row items-center gap-8">
                {/* Video Preview */}
                <div className="w-full lg:w-1/2 relative group">
                  {/* Static Poster Background */}
                  <div
                    className="aspect-video bg-cover bg-center rounded-xl cursor-pointer overflow-hidden relative"
                    style={{ backgroundImage: `url('/images/onboarding-video-poster.jpg')` }}
                    onClick={() => setIsTutorVideoModalOpen(true)}
                  >
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">

                      {/* Centered Play Button Wrapper */}
                      <div className="relative flex items-center justify-center">

                        {/* Pulse Animations */}
                        <div className="absolute w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-white/30 animate-ping opacity-75"></div>
                        <div
                          className="absolute w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-white/30 animate-ping opacity-50"
                          style={{ animationDelay: '1s' }}
                        ></div>

                        {/* Main Play Button */}
                        <div className="relative z-10 w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center transition-all duration-300 group-hover:bg-white/30">
                          <span className="ml-1 text-white text-2xl">▶</span>
                        </div>
                      </div>

                    </div>
                  </div>

                  <p className="mt-4 text-center text-sm opacity-90">
                    Watch: How you can create and publish your first course
                  </p>
                </div>


                {/* Text + CTA */}
                <div className="w-full lg:w-1/2 text-center lg:text-left">
                  <h3 className="text-2xl font-bold mb-4">Start Teaching in 3 Simple Steps</h3>
                  <ul className="space-y-3 mb-6 text-left max-w-md mx-auto lg:mx-0">
                    <li className="flex items-start gap-2">
                      <span className="text-primary-300 mt-1">✓</span>
                      <span>Submit your instructor application / Complete Onboarding</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary-300 mt-1">✓</span>
                      <span>Create your course with our easy to use course builder</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary-300 mt-1">✓</span>
                      <span>Publish/Wait for course approval and start earning from day one</span>
                    </li>
                  </ul>
                  <Link
                    to="/register"
                    state={{ userType: "instructor" }}
                    className="px-8 py-4 bg-primary-600 text-white font-medium text-lg rounded-lg shadow-lg hover:bg-primary-700  transition-transform transform hover:scale-105 hover:shadow-xl"
                  >
                    Apply Now — It's Free!
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action Section */}
        <section className="py-16 bg-primary-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Start Your Learning Journey?</h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto">
              Join BoleumLearn today and unlock your potential. With thousands of courses, expert instructors,
              and flexible learning options, your next career breakthrough is just a click away.
            </p>

            <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl p-8 max-w-4xl mx-auto mb-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
                <div className="text-center">
                  <div className="text-4xl mb-2">🎯</div>
                  <h3 className="text-lg font-semibold mb-2">Personalized Learning</h3>
                  <p className="text-neutral-600 dark:text-neutral-400">Learn at your own pace with courses tailored to your skill level</p>
                </div>
                <div className="text-center">
                  <div className="text-4xl mb-2">👨‍🏫</div>
                  <h3 className="text-lg font-semibold mb-2">Expert Guidance</h3>
                  <p className="text-neutral-600 dark:text-neutral-400">Get support from industry professionals and educators</p>
                </div>
                <div className="text-center">
                  <div className="text-4xl mb-2">🏆</div>
                  <h3 className="text-lg font-semibold mb-2">Career Advancement</h3>
                  <p className="text-neutral-600 dark:text-neutral-400">Boost your resume with recognized certificates and skills</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/register"
                  className="px-8 py-4 bg-primary-600 hover:bg-primary-700 text-white font-medium text-lg rounded-lg shadow-lg transition-transform transform hover:scale-105"
                >
                  Sign Up Now - It's Free!
                </Link>
                <Link
                  to="/courses"
                  className="px-8 py-4 bg-white text-primary-600 font-medium text-lg rounded-lg shadow-lg hover:bg-neutral-100 transition-transform transform hover:scale-105"
                >
                  Browse Courses
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main >

      <FloatingChatButton />
    </div >
  );
};

export default HomePage;